// Thin fetch wrapper for /api/* endpoints (which Caddy proxies to n8n webhooks).

import type {
  AddPhotosResponse,
  ApiError,
  ContactDocument,
  CreateDocumentArgs,
  CreateDocumentResponse,
  DatesResponse,
  EditorTokenResponse,
  GhlContact,
  GhlContext,
  PandaContact,
  PandaTemplate,
  PersonDocument,
  RevertToDraftResponse,
} from '../types';
import { getGhlContext } from '../ghl/sso';

const BASE = import.meta.env.VITE_API_BASE_URL || '';

function ghlHeaders(ghl: GhlContext | undefined, into: Record<string, string>): void {
  if (!ghl) return;
  // Forward GHL identity to the backend, which decrypts `encrypted` with the
  // SSO key for a trusted identity (falls back to the plain headers in grace).
  if (ghl.encrypted) into['x-ghl-sso'] = ghl.encrypted;
  if (ghl.locationId) into['x-ghl-location-id'] = ghl.locationId;
  if (ghl.userId) into['x-ghl-user-id'] = ghl.userId;
  if (ghl.email) into['x-ghl-user-email'] = ghl.email;
}

async function call<T>(path: string, init?: RequestInit & { ghl?: GhlContext; _retried?: boolean }): Promise<T> {
  const { ghl, headers, _retried, ...rest } = init || {};
  const finalHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((headers as Record<string, string>) || {}),
  };
  ghlHeaders(ghl, finalHeaders);

  const method = (rest as RequestInit).method || 'GET';
  const started = performance.now();
  console.info('[pdc] →', method, path);
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...rest, headers: finalHeaders });
  } catch (e) {
    // Network-level failure (server unreachable, connection reset, CORS, VPN
    // blocking the origin, offline). This is distinct from an HTTP error status.
    console.error('[pdc] ✗ NETWORK', method, path, `${Math.round(performance.now() - started)}ms`, e);
    throw e;
  }
  // SSO enforcement rejected us (missing/stale blob). Force a fresh handshake
  // and retry ONCE with the new blob before surfacing the error to the rep.
  if (res.status === 401 && !_retried) {
    console.warn('[pdc] 401 — forcing SSO re-handshake and retrying', method, path);
    const fresh = await getGhlContext(true);
    return call<T>(path, { ...init, ghl: fresh, _retried: true });
  }
  const ms = Math.round(performance.now() - started);
  if (!res.ok) {
    let body: ApiError | undefined;
    try {
      body = (await res.json()) as ApiError;
    } catch {
      // ignore
    }
    console.error('[pdc] ✗', method, path, res.status, `${ms}ms`, body?.error || body?.detail || '');
    throw new Error(body?.detail || body?.error || `${res.status} ${res.statusText}`);
  }
  console.info('[pdc] ✓', method, path, res.status, `${ms}ms`);
  // Some endpoints return an empty 200 body (a search that matched nothing).
  // Treat that as null, not an error.
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

export const api = {
  async searchContacts(q: string, ghl: GhlContext): Promise<GhlContact[]> {
    return (await call<GhlContact[] | null>(`/api/contacts/search?q=${encodeURIComponent(q)}`, { ghl })) ?? [];
  },

  getContact(contactId: string, ghl: GhlContext): Promise<GhlContact> {
    // Static path + id as query param — n8n doesn't reliably match a dynamic
    // `:id` segment when a static sibling (`/api/contacts/search`) exists.
    // respondToWebhook returns items as an array; unwrap to one object.
    return call<GhlContact | GhlContact[]>(`/api/contacts/get?id=${encodeURIComponent(contactId)}`, { ghl })
      .then((r) => (Array.isArray(r) ? r[0] : r));
  },

  async listTemplates(ghl: GhlContext): Promise<PandaTemplate[]> {
    return (await call<PandaTemplate[] | null>(`/api/templates`, { ghl })) ?? [];
  },

  // All PandaDoc documents for a contact (full history, from PandaDoc by metadata).
  async listContactDocuments(contactId: string, ghl: GhlContext): Promise<ContactDocument[]> {
    return (await call<ContactDocument[] | null>(
      `/api/contacts/documents?contactId=${encodeURIComponent(contactId)}`,
      { ghl },
    )) ?? [];
  },

  // Every PandaDoc document for a person — native (Salesforce-era) + GHL-created.
  // The backend merges a name search (MFR titles docs with the customer's name)
  // with a GHL-contact-id metadata search. `contactId` is optional.
  async listPersonDocuments(
    person: { name: string; email?: string; contactId?: string },
    ghl: GhlContext,
  ): Promise<PersonDocument[]> {
    const qs = new URLSearchParams({ name: person.name });
    if (person.email) qs.set('email', person.email);
    if (person.contactId) qs.set('contactId', person.contactId);
    return (await call<PersonDocument[] | null>(`/api/people/documents?${qs.toString()}`, { ghl })) ?? [];
  },

  // Download the signed PDF through an AUTHENTICATED request. The endpoint now
  // requires the SSO blob (audit C1: it used to be an open IDOR), so we can't use
  // a bare <a href> — we fetch the bytes with the GHL headers and hand the blob
  // to the browser as a save. Retries once on 401 with a fresh handshake.
  async downloadDocument(documentId: string, ghl: GhlContext): Promise<void> {
    const url = `${BASE}/api/documents/download?id=${encodeURIComponent(documentId)}`;
    const get = (ctx: GhlContext) => {
      const headers: Record<string, string> = {};
      ghlHeaders(ctx, headers);
      return fetch(url, { headers });
    };
    let res = await get(ghl);
    if (res.status === 401) res = await get(await getGhlContext(true));
    if (!res.ok) {
      let body: ApiError | undefined;
      try {
        body = (await res.json()) as ApiError;
      } catch {
        // ignore
      }
      throw new Error(body?.detail || body?.error || `${res.status} ${res.statusText}`);
    }
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') || '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    let name = `${documentId}.pdf`;
    if (m) {
      try {
        name = decodeURIComponent(m[1]) || name;
      } catch {
        name = m[1] || name; // a stray % that isn't a valid escape — use as-is
      }
    }
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  },

  // On a PandaDoc rejection the backend responds HTTP 502 with { error } and
  // `call()` throws that real message — so callers see the true reason. Returns
  // null only on an unexpected empty 200. Callers MUST null-check `.documentId`.
  createDocument(
    args: CreateDocumentArgs,
    ghl: GhlContext,
  ): Promise<CreateDocumentResponse | null> {
    // respondToWebhook returns items as an array; unwrap to one object.
    return call<CreateDocumentResponse | CreateDocumentResponse[] | null>(`/api/documents/create-from-template`, {
      method: 'POST',
      body: JSON.stringify(args),
      ghl,
    }).then((r) => (Array.isArray(r) ? r[0] ?? null : r));
  },

  // Upload an arbitrary file (PDF/DOCX/image) and create a PandaDoc document
  // from it — NO template. The rep then places the signature field and assigns
  // the signer in the embedded editor, and sends. Sent as multipart/form-data,
  // so this bypasses `call()` (the browser must set its own boundary; forcing
  // application/json would corrupt the upload). Returns { documentId } or throws
  // the real PandaDoc rejection reason (backend responds HTTP 502 { error }).
  async createDocumentFromFile(
    file: File,
    opts: { contactId: string; docName: string; signer?: { email: string; firstName?: string; lastName?: string } },
    ghl: GhlContext,
  ): Promise<CreateDocumentResponse | null> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('contactId', opts.contactId);
    fd.append('docName', opts.docName);
    if (opts.signer?.email) {
      fd.append('signerEmail', opts.signer.email);
      if (opts.signer.firstName) fd.append('signerFirst', opts.signer.firstName);
      if (opts.signer.lastName) fd.append('signerLast', opts.signer.lastName);
    }
    // GHL identity headers only — no Content-Type (let the browser set the
    // multipart boundary).
    const post = (ctx: GhlContext) => {
      const headers: Record<string, string> = {};
      ghlHeaders(ctx, headers);
      return fetch(`${BASE}/api/documents/create-from-file`, { method: 'POST', body: fd, headers });
    };
    let res = await post(ghl);
    if (res.status === 401) {
      // Same SSO-enforcement recovery as call(): re-handshake once and retry.
      res = await post(await getGhlContext(true));
    }
    if (!res.ok) {
      let body: ApiError | undefined;
      try {
        body = (await res.json()) as ApiError;
      } catch {
        // ignore
      }
      throw new Error(body?.detail || body?.error || `${res.status} ${res.statusText}`);
    }
    const text = await res.text();
    const r = text ? JSON.parse(text) : null;
    // respondToWebhook returns items as an array; unwrap to one object.
    return (Array.isArray(r) ? r[0] ?? null : r) as CreateDocumentResponse | null;
  },

  // Push job photos into the document's named image blocks.
  //
  // Deliberately NOT the editor's own upload zone: that one sends the bytes
  // from the browser through a third-party service, so an ad-blocker, a
  // corporate filter or a third-party-cookie policy kills it with no error the
  // rep can see, and it refuses the .heic an iPhone shoots by default. Here the
  // file goes to our backend, which converts it and hands PandaDoc a URL.
  async addPhotos(
    documentId: string,
    photos: File[],
    ghl: GhlContext,
    opts?: { slots?: string; reset?: boolean },
  ): Promise<AddPhotosResponse> {
    const post = (ctx: GhlContext) => {
      // A fresh FormData per attempt: a consumed body cannot be re-sent on the
      // 401 retry below.
      const fd = new FormData();
      // The id goes in the BODY, not the path: nginx routes this vhost by exact
      // location match, so /api/documents/<id>/photos could never be steered to
      // the backend. Same reason getContact() uses ?id= instead of /:id.
      fd.append('documentId', documentId);
      photos.forEach((p) => fd.append('files', p));
      if (opts?.slots) fd.append('slots', opts.slots);
      if (opts?.reset) fd.append('reset', 'true');
      const headers: Record<string, string> = {};
      ghlHeaders(ctx, headers); // no Content-Type — the browser sets the boundary
      return fetch(`${BASE}/api/documents/photos`, { method: 'POST', body: fd, headers });
    };
    let res = await post(ghl);
    if (res.status === 401) res = await post(await getGhlContext(true));
    if (!res.ok) {
      let body: ApiError | undefined;
      try {
        body = (await res.json()) as ApiError;
      } catch {
        // ignore
      }
      throw new Error(body?.detail || body?.error || `${res.status} ${res.statusText}`);
    }
    return (await res.json()) as AddPhotosResponse;
  },

  // Subcontractor directory for the Contractor section — pulled from PandaDoc's
  // own contacts (people the office has sent work orders to), filtered by `q`.
  async listPandaContacts(q: string, ghl: GhlContext): Promise<PandaContact[]> {
    return (await call<PandaContact[] | null>(
      `/api/pandadoc-contacts?q=${encodeURIComponent(q)}`,
      { ghl },
    )) ?? [];
  },

  getEditorToken(
    documentId: string,
    ghl: GhlContext,
  ): Promise<EditorTokenResponse> {
    // n8n's respondToWebhook returns items as an array; unwrap to a single object.
    return call<EditorTokenResponse | EditorTokenResponse[]>(`/api/documents/editor-token`, {
      method: 'POST',
      body: JSON.stringify({ documentId }),
      ghl,
    }).then((r) => (Array.isArray(r) ? r[0] : r));
  },

  // Pull a sent/viewed document back into draft so the editor can open it.
  // The backend refuses once anyone has signed (that would erase the signature).
  revertToDraft(documentId: string, ghl: GhlContext): Promise<RevertToDraftResponse> {
    return call<RevertToDraftResponse>(`/api/documents/revert-to-draft`, {
      method: 'POST',
      body: JSON.stringify({ documentId }),
      ghl,
    });
  },

  // Contract dates, read and written outside the embedded editor: on an iPad its
  // date boxes select-and-drag instead of opening a calendar.
  getDocumentDates(documentId: string, ghl: GhlContext): Promise<DatesResponse> {
    return call<DatesResponse>(`/api/documents/dates?id=${encodeURIComponent(documentId)}`, { ghl });
  },

  // `dates` maps a merge-field name to YYYY-MM-DD, or null to clear it.
  setDocumentDates(
    documentId: string,
    dates: Record<string, string | null>,
    ghl: GhlContext,
  ): Promise<DatesResponse> {
    return call<DatesResponse>(`/api/documents/dates`, {
      method: 'POST',
      body: JSON.stringify({ documentId, dates }),
      ghl,
    });
  },

  // Send the (already-edited) document to its recipients for signature.
  // The embedded editor has no Send action — PandaDoc separates editing from
  // sending — so we call the send API via n8n. `silent` suppresses recipient
  // email (used for testing); the UI sends with silent=false.
  // `name` renames the document right before sending (also becomes the PDF
  // filename in Google Drive); `message` goes into the recipient email.
  // Both fall back to backend defaults when omitted.
  sendDocument(
    documentId: string,
    ghl: GhlContext,
    opts: { silent?: boolean; name?: string; message?: string } = {},
  ): Promise<{ status?: string }> {
    return call(`/api/documents/send`, {
      method: 'POST',
      body: JSON.stringify({
        documentId,
        silent: opts.silent === true,
        name: opts.name || undefined,
        message: opts.message || undefined,
      }),
      ghl,
    });
  },
};
